/**
 * Profile Service
 * CRUD operations for user_profiles table
 */

import { supabase } from '@/config/supabase';
import type { UserProfile, UpdateProfileData, Address } from '@/types/profile.types';

/**
 * Fetch a user's profile
 */
export async function getProfile(userId: string): Promise<UserProfile | null> {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('user_id', userId)
    .single();

  if (error) {
    // PGRST116 = no rows found (profile not yet created)
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data as UserProfile;
}

/**
 * Update a user's profile
 */
export async function updateProfile(
  userId: string,
  updates: UpdateProfileData
): Promise<UserProfile> {
  const { data, error } = await supabase
    .from('user_profiles')
    .update(updates)
    .eq('user_id', userId)
    .select()
    .single();

  if (error) throw error;
  return data as UserProfile;
}

/**
 * Add a saved address to the user's profile
 */
export async function addSavedAddress(userId: string, address: Address): Promise<UserProfile> {
  const profile = await getProfile(userId);
  if (!profile) throw new Error('Profile not found');

  const savedAddresses = [...profile.saved_addresses, address];

  return updateProfile(userId, {
    default_address: profile.default_address ?? address,
  }).then(() =>
    supabase
      .from('user_profiles')
      .update({ saved_addresses: savedAddresses })
      .eq('user_id', userId)
      .select()
      .single()
      .then(({ data, error }) => {
        if (error) throw error;
        return data as UserProfile;
      })
  );
}

/**
 * Remove a saved address by index
 */
export async function removeSavedAddress(userId: string, index: number): Promise<UserProfile> {
  const profile = await getProfile(userId);
  if (!profile) throw new Error('Profile not found');

  const savedAddresses = profile.saved_addresses.filter((_, i) => i !== index);

  const { data, error } = await supabase
    .from('user_profiles')
    .update({ saved_addresses: savedAddresses })
    .eq('user_id', userId)
    .select()
    .single();

  if (error) throw error;
  return data as UserProfile;
}
